import React, { useState, useMemo } from 'react';
import { Search, Globe } from 'lucide-react';
import FilterModalWrapper from './FilterModalWrapper';

/**
 * CountryModal - Desktop modal for selecting countries
 * Provides searchable country list with flags
 * Adapted from mobile CountrySheet
 *
 * @param {boolean} isOpen - Whether modal is open
 * @param {Function} onClose - Callback to close modal
 * @param {string} selectedCountry - Currently selected country
 * @param {Function} onCountrySelect - Callback when country is selected
 * @param {Array} countries - List of countries
 */
const CountryModal = ({
  isOpen,
  onClose,
  selectedCountry,
  onCountrySelect,
  countries = []
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  // Default countries if none provided
  const defaultCountries = useMemo(() => [
    { code: 'BR', name: 'Brazil', flag: '🇧🇷', popular: true },
    { code: 'US', name: 'USA', flag: '🇺🇸', popular: true },
    { code: 'IT', name: 'Italy', flag: '🇮🇹', popular: true },
    { code: 'RU', name: 'Russia', flag: '🇷🇺', popular: true },
    { code: 'PL', name: 'Poland', flag: '🇵🇱', popular: true },
    { code: 'JP', name: 'Japan', flag: '🇯🇵', popular: true },
    { code: 'CN', name: 'China', flag: '🇨🇳' },
    { code: 'TR', name: 'Turkey', flag: '🇹🇷' },
    { code: 'DE', name: 'Germany', flag: '🇩🇪' },
    { code: 'FR', name: 'France', flag: '🇫🇷' },
    { code: 'AR', name: 'Argentina', flag: '🇦🇷' },
    { code: 'KR', name: 'South Korea', flag: '🇰🇷' },
  ], []);

  const countryList = countries.length > 0 ? countries : defaultCountries;

  // Group countries by popular/all
  const groupedCountries = useMemo(() => {
    const popular = countryList.filter(c => c.popular);
    const others = countryList.filter(c => !c.popular);

    return { popular, others };
  }, [countryList]);

  // Filter countries by search query
  const filteredCountries = useMemo(() => {
    if (!searchQuery) return groupedCountries;

    const query = searchQuery.toLowerCase();
    const filterList = (list) => list.filter(country => {
      const name = (country.name || '').toLowerCase();
      const code = (country.code || '').toLowerCase();
      return name.includes(query) || code.includes(query);
    });

    return {
      popular: filterList(groupedCountries.popular),
      others: filterList(groupedCountries.others)
    };
  }, [groupedCountries, searchQuery]);

  const handleCountrySelect = (country) => {
    onCountrySelect(country.name || country.code);
    setSearchQuery('');
  };

  const handleClearSelection = () => {
    onCountrySelect('');
    setSearchQuery('');
  };

  return (
    <FilterModalWrapper
      isOpen={isOpen}
      onClose={onClose}
      title="Select Country"
      icon={Globe}
      maxWidth="md"
      maxHeight="70vh"
    >
      {/* Search Bar */}
      <div className="p-4 border-b border-white/10">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search countries..."
            className="w-full pl-10 pr-4 py-2 bg-gray-800 text-white rounded-lg text-sm
                     focus:outline-none focus:ring-2 focus:ring-emerald-500
                     border border-white/10"
          />
        </div>
      </div>

      {/* Country List */}
      <div className="overflow-y-auto">
        {/* All Countries Option */}
        <button
          onClick={handleClearSelection}
          className={`
            w-full flex items-center gap-3 px-4 py-3 transition-colors
            ${!selectedCountry || selectedCountry === ''
              ? 'bg-emerald-500/20 text-emerald-400 border-l-4 border-emerald-500'
              : 'hover:bg-gray-800/50 text-white'
            }
          `}
        >
          <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center text-lg">
            🌍
          </div>
          <span className="font-medium">All Countries</span>
          {(!selectedCountry || selectedCountry === '') && (
            <div className="ml-auto w-2 h-2 rounded-full bg-emerald-400" />
          )}
        </button>

        {/* Popular Countries */}
        {filteredCountries.popular.length > 0 && (
          <div>
            <div className="px-4 py-2 bg-gray-800/50 border-y border-white/5">
              <span className="text-xs font-semibold text-gray-400 uppercase">
                Popular
              </span>
            </div>

            {filteredCountries.popular.map((country) => {
              const isSelected = selectedCountry === country.name || selectedCountry === country.code;

              return (
                <button
                  key={country.code}
                  onClick={() => handleCountrySelect(country)}
                  className={`
                    w-full flex items-center gap-3 px-4 py-3 transition-colors
                    ${isSelected
                      ? 'bg-emerald-500/20 text-emerald-400 border-l-4 border-emerald-500'
                      : 'hover:bg-gray-800/50 text-white'
                    }
                  `}
                >
                  <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center text-lg">
                    {country.flag}
                  </div>

                  <div className="flex-1 text-left">
                    <div className="font-medium">{country.name}</div>
                    <div className="text-xs text-gray-500">{country.code}</div>
                  </div>

                  {isSelected && (
                    <div className="w-2 h-2 rounded-full bg-emerald-400" />
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* Other Countries */}
        {filteredCountries.others.length > 0 && (
          <div>
            {filteredCountries.popular.length > 0 && (
              <div className="px-4 py-2 bg-gray-800/50 border-y border-white/5">
                <span className="text-xs font-semibold text-gray-400 uppercase">
                  All Countries
                </span>
              </div>
            )}

            {filteredCountries.others.map((country) => {
              const isSelected = selectedCountry === country.name || selectedCountry === country.code;

              return (
                <button
                  key={country.code}
                  onClick={() => handleCountrySelect(country)}
                  className={`
                    w-full flex items-center gap-3 px-4 py-3 transition-colors
                    ${isSelected
                      ? 'bg-emerald-500/20 text-emerald-400 border-l-4 border-emerald-500'
                      : 'hover:bg-gray-800/50 text-white'
                    }
                  `}
                >
                  <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center text-lg">
                    {country.flag}
                  </div>

                  <div className="flex-1 text-left">
                    <div className="font-medium">{country.name}</div>
                    <div className="text-xs text-gray-500">{country.code}</div>
                  </div>

                  {isSelected && (
                    <div className="w-2 h-2 rounded-full bg-emerald-400" />
                  )}
                </button>
              );
            })}
          </div>
        )}

        {filteredCountries.popular.length === 0 && filteredCountries.others.length === 0 && (
          <div className="p-8 text-center">
            <p className="text-gray-500">No countries found</p>
          </div>
        )}
      </div>
    </FilterModalWrapper>
  );
};

export default CountryModal;
